import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  CheckboxInput,
  RadioInput,
  SelectInput,
  TextareaInput,
  TextInput,
} from '@internal/kern-ux';

/**
 * KERN form fields for software templates.
 *
 * The scaffolder renders forms via react-jsonschema-form with Material UI widgets. These
 * fields replace them with KERN markup — templates opt in via `ui:field`, e.g.
 * `ui:field: KernText`.
 *
 * All fields link label, hint and error message via the input's ID, so that screen
 * readers announce them together (WCAG 1.3.1 / 3.3.2).
 */

/** Picks the first usable message from the raw RJSF errors. */
const firstError = (rawErrors?: string[]) =>
  rawErrors && rawErrors.length > 0 ? rawErrors[0] : undefined;

export const KernText = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  idSchema,
  disabled,
}: FieldExtensionComponentProps<string>) => (
  <TextInput
    id={idSchema?.$id ?? 'kern-text'}
    label={schema.title}
    hintText={schema.description}
    error={firstError(rawErrors)}
    required={required}
    disabled={disabled}
    hasOptionalLabel={!required}
    value={formData ?? ''}
    onChange={event => onChange(event.target.value)}
  />
);

export const KernTextarea = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  idSchema,
  disabled,
}: FieldExtensionComponentProps<string>) => (
  <TextareaInput
    id={idSchema?.$id ?? 'kern-textarea'}
    label={schema.title}
    hintText={schema.description}
    error={firstError(rawErrors)}
    required={required}
    disabled={disabled}
    hasOptionalLabel={!required}
    value={formData ?? ''}
    onChange={event => onChange(event.target.value)}
  />
);

export const KernSelect = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  idSchema,
  disabled,
}: FieldExtensionComponentProps<string>) => {
  // Options come from the JSON schema enum.
  const options = (schema.enum ?? []).map(value => ({
    value: String(value),
    label: String(value),
  }));

  return (
    <SelectInput
      id={idSchema?.$id ?? 'kern-select'}
      label={schema.title}
      hintText={schema.description}
      errorMessage={firstError(rawErrors)}
      required={required}
      disabled={disabled}
      hasOptionalLabel={!required}
      options={options}
      value={formData ?? ''}
      onChange={event => onChange(event.target.value)}
    />
  );
};

export const KernCheckbox = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  idSchema,
  disabled,
}: FieldExtensionComponentProps<boolean>) => (
  <CheckboxInput
    id={idSchema?.$id ?? 'kern-checkbox'}
    label={schema.title}
    error={firstError(rawErrors)}
    required={required}
    disabled={disabled}
    checked={Boolean(formData)}
    onChange={event => onChange(event.target.checked)}
  />
);

export const KernRadio = ({
  onChange,
  rawErrors,
  formData,
  schema,
  idSchema,
  disabled,
}: FieldExtensionComponentProps<string>) => {
  const options = (schema.enum ?? []).map(value => String(value));
  const groupId = idSchema?.$id ?? 'kern-radio';
  const errorMessage = firstError(rawErrors);

  return (
    <fieldset
      className={`kern-fieldset${errorMessage ? ' kern-fieldset--error' : ''}`}
      aria-describedby={errorMessage ? `${groupId}-error` : undefined}
    >
      <legend className="kern-label">{schema.title}</legend>
      {schema.description && (
        <p className="kern-hint" id={`${groupId}-hint`}>
          {schema.description}
        </p>
      )}
      <div className="kern-fieldset__body">
        {options.map(option => (
          <RadioInput
            key={option}
            id={`${groupId}-${option}`}
            name={groupId}
            label={option}
            value={option}
            disabled={disabled}
            error={Boolean(errorMessage)}
            checked={formData === option}
            onChange={() => onChange(option)}
          />
        ))}
      </div>
      {errorMessage && (
        <p className="kern-error" id={`${groupId}-error`} role="alert">
          {errorMessage}
        </p>
      )}
    </fieldset>
  );
};
